// JSON API client. Mutations always send Content-Type: application/json; the
// browser adds the Origin header the server's CSRF check requires.

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface ApiOptions {
  /** On 401, go to the sign-in page (default true). */
  redirectOn401?: boolean;
}

export async function api<T>(method: string, path: string, body?: unknown, opts: ApiOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: method === 'GET' ? {} : { 'Content-Type': 'application/json' },
      body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiError(0, "Can't reach the server. Check your connection.");
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    if (res.status === 401 && (opts.redirectOn401 ?? true)) goToSignIn();
    throw new ApiError(res.status, data.error ?? `Request failed (${res.status}).`);
  }
  return data;
}

export const get = <T>(path: string, opts?: ApiOptions) => api<T>('GET', path, undefined, opts);
export const post = <T>(path: string, body?: unknown, opts?: ApiOptions) => api<T>('POST', path, body, opts);
export const put = <T>(path: string, body?: unknown, opts?: ApiOptions) => api<T>('PUT', path, body, opts);
export const patch = <T>(path: string, body?: unknown, opts?: ApiOptions) => api<T>('PATCH', path, body, opts);
export const del = <T>(path: string, opts?: ApiOptions) => api<T>('DELETE', path, undefined, opts);

const NEXT_KEY = 'signin-next';

/** Remembers where to return after sign-in (survives the round trip through email). */
export function goToSignIn(): void {
  const here = location.pathname + location.search;
  try {
    if (here !== '/' && !here.startsWith('/login')) localStorage.setItem(NEXT_KEY, here);
  } catch { /* storage unavailable: fall back to home after sign-in */ }
  location.assign('/login');
}

/** Where to go after signing in: a remembered same-origin path, or home. */
export function takeNextPath(): string {
  let next: string | null = null;
  try {
    next = localStorage.getItem(NEXT_KEY);
    localStorage.removeItem(NEXT_KEY);
  } catch { /* ignore */ }
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}
